import MultipleSelect, {
  IMultipleSelectType,
} from '../components/MultipleSelect';
import { MultipleSelectButton } from '../components/MultipleSelect/MultipleSelectButton';
import { TComponentConfig } from '../types';
import { getMultipleSelectDefaultValue } from '../utils';

/**
 * cond 接口返回项格式（B 站 channels / video_tags 统一约定）：
 *   [{ value: 'xxx', label_en: 'xxx', label_zh: 'xxx' }, ...]
 *
 * 转换为 FilterCommon 内部的 TOptionsItem 形态：
 *   { value, text: label_en, textCn: label_zh }
 *
 * 注意：cond 会下发 {value:'all', label_zh:'全部'} 作为 B 站真实的"综合榜分区"
 *   （后端 channel_name_en='all'，是一个具体分区，不是"不过滤"标记）。
 *   该项保留为正常选项发给后端；为避免 sentinel 碰撞，配置侧需 allOption:false 禁用
 *   tea-component 默认的 "All" 切换按钮，改用 MultipleSelect 的 tips 区域 Select All/Clear All。
 */
function condItemsToOptions(condInfo: any, key: string) {
  const list = condInfo?.[key];
  if (!Array.isArray(list)) return [];
  return list
    .filter((item: any) => item?.value !== 'all')
    .map((item: any) => ({
      value: item?.value,
      text: item?.label_en ?? item?.value ?? '',
      textCn: item?.label_zh ?? item?.label_en ?? item?.value ?? '',
    }));
}

/**
 * 默认值：cond 加载完成后默认全选（UI 显示 "All / 全部"）。
 *
 * 注意第 4 个参数必须是 `list.map(i => i.value)`（即所有 option 的 value），
 * 而不是 `[]`。否则 UI 会显示"0 selected"而非"All"。
 * 这是 MemeType / ADType / CompetitorsAd 等所有多选过滤器的标准模式。
 */
function defaultValueFunc(filterValue: any, props: IMultipleSelectType) {
  const { options, valueKeys: key } = props;
  const list = options || [];
  return getMultipleSelectDefaultValue(
    filterValue,
    key,
    list,
    list.map((item) => item.value),
  );
}

/** 全不选哨兵值（与 getMultipleSelectDefaultValueWithSpecialCase 约定一致） */
const UNSELECT_ALL_VALUE = '-99999';

/**
 * 提交值（backValue）三态约定：
 * - 全选（length === options.length）→ `[]`（"不过滤"语义，API channel: []）
 * - 全不选（length === 0）         → `['-99999']`（哨兵值，表示用户主动清空，需后端返回空结果）
 * - 部分选中                        → `string[]`（IN 匹配）
 *
 * 配合 defaultValueFunc：UI 初始全选 → 用户清空时此处归一为 ['-99999'] → API 明确不返回数据。
 */
function backValueFunc(filterValue: any, props: IMultipleSelectType) {
  const { valueKeys: key, options } = props;
  const list = (options || []).map((item) => item.value);
  const values = filterValue?.[key] || [];
  if (values.length === list.length) {
    return { [key]: [] };
  }
  if (values.length === 0) {
    return { [key]: [UNSELECT_ALL_VALUE] };
  }
  return { [key]: values };
}

/**
 * B 站分区筛选器（多选 + Select All / Clear All）
 *
 * 选项来源：cond 接口 `marketing_hub_bilibili_video_config_channels`
 *
 * 关键设计：
 * - `allOption: false` —— 禁用 tea-component 内置的 "All" 切换行。
 *   原因：cond 含 {value:'all', label_zh:'全部'} 作为真实的 B 站综合榜分区，必须保留为
 *   可选项；若不禁用，会与 tea allOption.value='all' 发生 sentinel 碰撞导致默认 All 文案
 *   显示异常。
 * - `showTips + hasSelectAll + hasClearAll` —— 改用 MultipleSelect 自带 tips 区域里的
 *   Select All / Clear All 切换按钮，逻辑直接调 change(values) / change([])，与 cond 项
 *   解耦。
 *
 * 用户操作 → API：
 * - 全选所有分区（默认）→ backValue 归一为 `[]` → API channel: [] (不过滤)
 * - 仅选 'all'        → backValue 透传      → API channel: ['all'] (B 站综合榜分区)
 * - 部分选中          → backValue 透传      → API channel: ['game', ...] (IN 匹配)
 * - 全清空            → backValue ['-99999'] → 哨兵，后端返回空
 */
export const BilibiliCategory: TComponentConfig<IMultipleSelectType> = {
  Component: MultipleSelect,
  ButtonComponent: MultipleSelectButton,
  requestKey: 'marketing_hub_bilibili_video_config_channels',
  condDealwith: condItemsToOptions,
  defaultValue: defaultValueFunc,
  backValue: backValueFunc,
  props: {
    title: 'Category',
    titleCn: '类型',
    valueKeys: 'channel',
    options: [],
    allOption: false,
    showTips: true,
    hasSelectAll: true,
    hasClearAll: true,
    searchable: true,
    listHeight: 300,
    listWidth: 250,
    matchButtonWidth: false,
    overlayClassName: 'dbom-bili-category-overlay',
  },
};

/**
 * B 站视频标签筛选器（多选 + Select All / Clear All）
 *
 * 选项来源：cond 接口 `marketing_hub_bilibili_video_config_video_tags`
 *
 * 配置同 BilibiliCategory，禁用 tea 内置 allOption（防御性 — 即便当前 tag cond 不含
 * value='all' 的项，未来若加也不会引发 sentinel 碰撞）。
 *
 * ⚠️ 后端契约提示：当前 API 文档（api/list.md）video_tag 字段类型为 `string`，仅支持单值。
 *    本前端按多选实现，发送 `video_tag: string[]`；需后端将 video_tag 升级支持数组
 *    （建议 `[]string` 语义同 channel：空数组 = 不过滤；多值 IN 匹配）。
 */
export const BilibiliTag: TComponentConfig<IMultipleSelectType> = {
  Component: MultipleSelect,
  ButtonComponent: MultipleSelectButton,
  requestKey: 'marketing_hub_bilibili_video_config_video_tags',
  condDealwith: condItemsToOptions,
  defaultValue: defaultValueFunc,
  backValue: backValueFunc,
  props: {
    title: 'Tag',
    titleCn: '标签',
    valueKeys: 'video_tag',
    options: [],
    allOption: false,
    showTips: true,
    hasSelectAll: true,
    hasClearAll: true,
    matchButtonWidth: false,
    overlayClassName: 'dbom-bili-tag-overlay',
    searchable: true,
    listHeight: 300,
    listWidth: 250,
  },
};
